import { createContext, useCallback, useContext, useMemo, useState } from 'react';

interface AccordionGroupContextValue {
  /** Increments when "Expand all" is fired. Listeners snap to expanded. */
  expandSignal: number;
  /** Increments when "Collapse all" is fired. Listeners snap to collapsed. */
  collapseSignal: number;
}

const AccordionGroupContext = createContext<AccordionGroupContextValue | null>(null);

interface AccordionGroupProviderProps {
  children: React.ReactNode;
}

/**
 * Lightweight broadcast for "expand all / collapse all" across a group of
 * `WizardAccordion`s. Children that read `useAccordionGroupSignals()` snap
 * their expanded state to match each time a signal increments.
 *
 * Children stay independently toggleable in between signals — this is a
 * one-way broadcast, not lifted state.
 */
export const AccordionGroupProvider = ({ children }: AccordionGroupProviderProps) => {
  const [expandSignal, setExpandSignal] = useState(0);
  const [collapseSignal, setCollapseSignal] = useState(0);

  const expandAll = useCallback(() => setExpandSignal((n) => n + 1), []);
  const collapseAll = useCallback(() => setCollapseSignal((n) => n + 1), []);

  const ctx = useMemo<AccordionGroupContextValue>(
    () => ({ expandSignal, collapseSignal }),
    [expandSignal, collapseSignal],
  );

  return (
    <AccordionGroupContext.Provider value={ctx}>
      <AccordionGroupBindingsContext.Provider value={{ expandAll, collapseAll }}>
        {children}
      </AccordionGroupBindingsContext.Provider>
    </AccordionGroupContext.Provider>
  );
};

interface AccordionGroupBindings {
  expandAll: () => void;
  collapseAll: () => void;
}

const AccordionGroupBindingsContext = createContext<AccordionGroupBindings | null>(null);

/** Returns expand/collapse-all callbacks. Call from the toolbar. */
export const useAccordionGroupActions = (): AccordionGroupBindings => {
  const ctx = useContext(AccordionGroupBindingsContext);
  if (!ctx) {
    throw new Error('useAccordionGroupActions must be used inside AccordionGroupProvider');
  }
  return ctx;
};

/** Returns the current signal counters. Returns null when no provider is present. */
export const useAccordionGroupSignals = (): AccordionGroupContextValue | null => {
  return useContext(AccordionGroupContext);
};
